import { Popover } from 'antd';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { Dropdown, Flex, Icon } from 'ui';
import {
  addDataToNoneView,
  sortParamToCOPSModel,
  transformFiltersToUrlParams,
} from 'utils/helpers';
import { withRouter } from 'utils/withRouter';
import { boardIdTypes } from '../../../common/gantt/config/view';
import { sortTypes, ticketWorklistId, viewTypes } from '../../../utils/app-configs';

const ViewFlex = styled(Flex)`
  margin-left: ${p => p.isPlanningBoard ? '10px' : '15px'};
  flex-flow: row nowrap;
  font-size: 13px;
  height: 34px;
`;

const Title = styled.p`
  padding-top: 8px;
`;

const StyledDropdown = styled(Dropdown)`
  margin-left: 10px;
`;

const StyledIcon = styled(Icon)`
  font-size: 20px;
  margin: 0 10px;
  margin-top: 6px;
  cursor: ${p => p.disabled ? 'not-allowed' : 'pointer'};
  color: ${p => p.disabled ? p.theme.colors.themeSecondary : p.theme.colors.theme};
  opacity: ${p => p.disabled ? 0.3 : 1};
  transition: all 0.5s linear;
  transform-origin: center;
  width: 20px !important;
  height: 20px !important;
  transform: ${p => p.isOpen ? 'rotate(60deg)' : 'rotate(-60deg)'};
`;

const StyledMenuIcon = styled(Icon)`
  margin-right: 5px;
`;

const ViewAction = styled.p`
  cursor: pointer;
  padding: 4px 16px;
  &:hover {
    background-color: #0a5c75 !important;
    border-radius: 4px;
  }
`;

const ViewDropdown = ({
  intl,
  isLoading,
  isLoadingViews,
  views,
  match,
  history,
  range,
  updateView,
  activeView,
  activeFilters,
  activeSort,
  updateViews,
  isSavingViews,
  isPlanningBoard = false,
  isTickets = false,
  defaultExpanded,
}) => {
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [viewValue, setViewValue] = useState();
  const [viewsArray, setViewsArray] = useState([]);
  const { defaultSorting } = useSelector(state => state.worklist.metaData);
  const allFSEs = useSelector(state => state.gantt.allFSEsList);
  const createUrl = isPlanningBoard ? 'create-gantt-view' : 'create-view';
  const manageUrl = isPlanningBoard ? 'manage-gantt-views' : 'manage-views';

  const getId = () => {
    if (isPlanningBoard) {
      return boardIdTypes.service;
    } else if (isTickets) {
      return ticketWorklistId;
    }
    return match.params.worklistId;
  };

  // Don't use deepcompare here since views can be null to avoid a rerender
  // Track issue: https://github.com/kentcdodds/use-deep-compare-effect/issues/63
  useEffect(() => {
    if (views) {
      setViewsArray(addDataToNoneView(
        getId(),
        views,
        null,
        isPlanningBoard ? null : defaultSorting,
      ));
    }
  }, [JSON.stringify(views)]);


  const handleViewChange = (e) => {
    setViewValue(e);
    const selectedView = viewsArray?.find(v => v.id === e);
    updateView(selectedView);
  };

  useEffect(() => {
    setViewValue(activeView?.id);
  }, [activeView?.id]);

  const getDropdownValue = () => {
    if (isLoadingViews) {
      return intl.formatMessage({ id: 'worklist.filters.DATA_LOADING' });
    }
    return null;
  };

  const onOptionSelect = (urlParam) => {
    history.push(`${urlParam}`);
    setTooltipOpen(!tooltipOpen);
  };

  /**
   * This function updates only one view.
   * This is the case when a user wants to add
   * an extra filter to an existing view.
   */
  const updateSingleView = () => {
    const editedViewsArray = viewsArray?.filter(v => !v?.isNone)?.map((v) => {
      if (v.id === activeView?.id) {
        const sort = sortParamToCOPSModel(activeSort);
        const mappedSorting = sort?.map(s => ({
          ...s,
          manualSort: s.sortDirection === sortTypes.manual ? allFSEs : [],
        }));
        const view = {
          ...v,
          sorting: mappedSorting,
          filterData: transformFiltersToUrlParams(activeFilters),
          viewRange: isPlanningBoard ? range?.id : null,
          isExpanded: defaultExpanded,
          columnInfo: JSON.parse(localStorage.getItem('columnParams')),
          childColumnInfo: JSON.parse(localStorage.getItem('childColumnParams')),
        };
        return view;
      }
      return v;
    });

    setTooltipOpen(!tooltipOpen);
    // update views
    updateViews({ id: getId(), views: editedViewsArray });
  };

  const renderPopOverContent = () =>
    <>
      <ViewAction
        onClick={() => onOptionSelect(createUrl)}
      >
        <FormattedMessage id='worklist.filters.SAVE_VIEW' />
      </ViewAction>
      <ViewAction
        onClick={() => onOptionSelect(manageUrl)}
      >
        <FormattedMessage id='worklist.filters.MANAGE_VIEW' />
      </ViewAction>
      {(activeView?.isNone || !activeView?.editable) ||
        <ViewAction
          onClick={() => updateSingleView()}
        >
          <FormattedMessage id='worklist.filters.UPDATE_VIEW' />
        </ViewAction>
      }
    </>;

  const getViewIcon = (v) => {
    if (v.templateType === viewTypes.system) {
      return <StyledMenuIcon type='globe-europe' solid={true} />;
    } else if (v.templateType === viewTypes.campaign) {
      return <StyledMenuIcon type='bullseye-pointer' solid={true} />;
    }
    return null;
  };
  const sort = { [viewTypes.system]: 1, [viewTypes.campaign]: 2, [viewTypes.user]: 3 };
  return (
    <ViewFlex isPlanningBoard={isPlanningBoard} data-test-id='view-template'>
      <Title>
        <FormattedMessage id='worklist.filters.VIEW' />
        {':'}
      </Title>
      <StyledDropdown
        width={isPlanningBoard ? '145px' : '250px'}
        dropdownMatchSelectWidth={!isPlanningBoard}
        data-test-id='view-template-dropdown'
        placeholder={intl.formatMessage({
          id: 'worklist.filters.NO_LABEL',
        })}
        onSelect={handleViewChange}
        values={viewsArray
          ?.sort((a, b) => sort[a.templateType] - sort[b.templateType])
          ?.map(v => ({
            ...v,
            templateName: <span>
              {getViewIcon(v)}{v.templateName}
            </span>,
          }))}
        disabled={isLoading || isLoadingViews || isSavingViews}
        valueLabel='templateName'
        valueKey='id'
        loading={isLoading || isLoadingViews || isSavingViews}
        value={getDropdownValue() || viewValue}
        filterOption={(value, option) =>
          option.children?.props?.children?.length >= 2 && option.children?.props?.children[1]
            ?.toString()
            ?.toLowerCase()
            ?.indexOf(value.toLowerCase()) >= 0
        }
      />
      <Popover
        content={renderPopOverContent()}
        trigger='click'
        placement='bottomRight'
        arrowPointAtCenter={true}
        open={tooltipOpen}
        onVisibleChange={() => !isLoading && !isLoadingViews && setTooltipOpen(!tooltipOpen)}
        overlayClassName='view_popover'
        getPopupContainer={triggerNode => triggerNode.parentNode}
      >
        <StyledIcon
          type='cog'
          isOpen={tooltipOpen}
          disabled={isLoading || isLoadingViews || isSavingViews}
        />
      </Popover>
    </ViewFlex>
  );
};

ViewDropdown.propTypes = {
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired,
  }),
  isLoading: PropTypes.bool,
  isLoadingViews: PropTypes.bool,
  views: PropTypes.array,
  updateView: PropTypes.func,
  match: PropTypes.object,
  history: PropTypes.object,
  activeView: PropTypes.object,
  activeFilters: PropTypes.array,
  activeSort: PropTypes.string,
  updateViews: PropTypes.func,
  isSavingViews: PropTypes.bool,
  isPlanningBoard: PropTypes.bool,
  isTickets: PropTypes.bool,
  range: PropTypes.string,
  defaultExpanded: PropTypes.bool,
};

export default withRouter(injectIntl(ViewDropdown));
